import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TickOverlay } from './TickOverlay';
import { subjectBorderClass } from '../../shared/constants/subjects';
import { subjectAbbrev } from '../../shared/constants/subjects';
import { api } from '../../shared/utils/api';
import ROUTES from '../../shared/constants/routes';

// ─── Border class helper ──────────────────────────────────────────────────────

function borderClass(entry, colourMode) {
  if (colourMode === 'subject') return subjectBorderClass(entry.subject_area);
  const s = entry.status;
  if (s === 'completed') return 'border-completed';
  if (s === 'moved')     return 'border-moved';
  const t = entry.planner_type;
  if (t === 'new') return 'border-new';
  // revision — colour by confidence
  const conf = entry.topic_confidence;
  if (conf == null || conf < 0.4) return 'border-revision-weak';
  if (conf < 0.7) return 'border-revision-moderate';
  return 'border-revision-strong';
}

// ─── Knowledge dot ────────────────────────────────────────────────────────────

function KnowledgeDot({ conf }) {
  let cls = 'knowledge-dot-none';
  if (conf != null) {
    if (conf < 0.4)  cls = 'knowledge-dot-weak';
    else if (conf < 0.7) cls = 'knowledge-dot-moderate';
    else cls = 'knowledge-dot-strong';
  }
  return <div className={`knowledge-dot ${cls}`} />;
}

// ─── Size bars ────────────────────────────────────────────────────────────────

function SizeBars({ principleCount }) {
  const pc = principleCount || 0;
  let tier = 'small';
  if (pc >= 15) tier = 'large';
  else if (pc >= 8) tier = 'medium';

  return (
    <div className="size-bars">
      <div className={`size-bar size-bar-1 ${tier === 'small' || tier === 'medium' || tier === 'large' ? `size-bar-${tier}` : 'size-bar-off'}`} />
      <div className={`size-bar size-bar-2 ${tier === 'medium' || tier === 'large' ? `size-bar-${tier}` : 'size-bar-off'}`} />
      <div className={`size-bar size-bar-3 ${tier === 'large' ? 'size-bar-large' : 'size-bar-off'}`} />
    </div>
  );
}

// ─── Confidence value colour ──────────────────────────────────────────────────

function confColourClass(conf) {
  if (conf >= 0.7) return 'conf-green';
  if (conf >= 0.4) return 'conf-amber';
  return 'conf-red';
}

// ─── Last seen formatter ──────────────────────────────────────────────────────

function formatLastSeen(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return '1d ago';
  if (diff < 30) return `${diff}d ago`;
  if (diff < 365) return `${Math.floor(diff / 30)}mo ago`;
  return `${Math.floor(diff / 365)}y ago`;
}

function lastSeenColour(dateStr) {
  if (!dateStr) return 'card-stat-muted';
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 86400000);
  if (diff <= 7)  return 'card-stat-green';
  if (diff <= 21) return 'card-stat-amber';
  return 'card-stat-red';
}

// ─── MCQ bar ─────────────────────────────────────────────────────────────────

function McqBar({ correct, total }) {
  if (!total) return null;
  const segs = Array.from({ length: total }, (_, i) => (
    <span key={i} className={`mcq-seg ${i < correct ? 'mcq-seg-correct' : 'mcq-seg-wrong'}`} />
  ));
  return <span className="mcq-bar">{segs}</span>;
}

// ─── DayCard v6 ───────────────────────────────────────────────────────────────

export default function DayCard({ entry, dateStr, allWeekDates, onComplete, onMove, isPast, detailLevel, colourMode, onNavigateToTopic }) {
  const navigate = useNavigate();
  const [completing, setCompleting] = useState(false);
  const [localEntry, setLocalEntry] = useState(entry);
  useEffect(() => setLocalEntry(entry), [entry]);

  const e = localEntry;
  const isCompleted = e.status === 'completed';
  const isMoved     = e.status === 'moved';
  const isRevision  = e.planner_type === 'revision';
  const conf        = e.topic_confidence;
  const abbrev      = subjectAbbrev(e.subject_area);
  const dayIndex    = allWeekDates.indexOf(dateStr);
  const canTick     = !isCompleted && !isMoved;   // tick works on any date
  const canMove     = !isPast && !isCompleted && !isMoved;  // move only on present/future
  const canNavigate = !!(onNavigateToTopic && e.topic_id);
  const level       = detailLevel || 'standard';
  const colour      = colourMode  || 'status';
  const border      = borderClass(e, colour);

  const handleConfirm = async (confVal, notes) => {
    const body = { schedule_entry_id: e.id, confidence: confVal };
    if (notes !== undefined && notes !== null) body.notes = notes;
    const res = await api('/api/planner/complete', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    if (res.ok) {
      setLocalEntry(prev => ({ ...prev, status: 'completed', confidence_on_complete: confVal }));
      onComplete(e.id, confVal, res.overall_progress);
    }
    setCompleting(false);
  };

  const clickProps = canNavigate
    ? { onClick: () => onNavigateToTopic(e.topic_id, e.module), style: { cursor: 'pointer' } }
    : {};

  // ── Completed ──
  if (isCompleted) {
    const confDone = e.confidence_on_complete ?? 0;
    const confPct  = Math.round(confDone * 100);
    return (
      <div className={`day-card-completed ${border}`}>
        <div className="card-body">
          {/* Row 1: name + tick + conf */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 4 }}>
            <span className="card-name-done">{e.subtopic || e.display_name}</span>
            <div className="done-tick" style={{ flexShrink: 0 }}>
              <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
            </div>
            {e.confidence_on_complete != null && (
              <span className="done-conf">{confPct}%</span>
            )}
          </div>
          {/* Row 2: knowledge dot + subject */}
          <div className="card-row2">
            <div className="knowledge-dot knowledge-dot-strong" />
            <span className="card-subject">{abbrev}</span>
          </div>
          {/* Row 3: L2 text */}
          {e.general_topic && (
            <div className="card-l2-row">{e.general_topic}</div>
          )}
        </div>
      </div>
    );
  }

  // ── Moved ──
  if (isMoved) {
    return (
      <div className={`day-card-moved ${border}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1 }}>
          <span className="card-name-moved">{e.subtopic || e.display_name}</span>
          <span className="moved-label">Missed</span>
        </div>
      </div>
    );
  }

  // ── Pending (new or revision) ──
  const cardClass = isRevision ? `day-card-revision ${border}` : `day-card-new ${border}`;

  const actionButtons = canTick ? (
    <>
      <button className="btn-tick" onClick={ev => { ev.stopPropagation(); setCompleting(true); }} title="Mark as done">
        <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>
      </button>
      {canMove && (
        <div className="card-arrows">
          <button className="btn-arrow" disabled={dayIndex <= 0}
            onClick={ev => { ev.stopPropagation(); dayIndex > 0 && onMove(e.id, allWeekDates[dayIndex - 1]); }}>
            <svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button className="btn-arrow" disabled={dayIndex >= allWeekDates.length - 1}
            onClick={ev => { ev.stopPropagation(); dayIndex < allWeekDates.length - 1 && onMove(e.id, allWeekDates[dayIndex + 1]); }}>
            <svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </div>
      )}
    </>
  ) : null;

  return (
    <>
      <div className={cardClass} {...clickProps}>
        <div className="card-body">
          {/* Row 1: name + pill/conf */}
          <div className="card-row1">
            <span className="card-name">{e.subtopic || e.display_name}</span>
            {isRevision && conf != null ? (
              <span className={`conf-value ${confColourClass(conf)}`}>{Math.round(conf * 100)}%</span>
            ) : !isRevision ? (
              <span className="pill-new">New</span>
            ) : null}
          </div>

          {/* Row 2: knowledge dot + subject */}
          <div className="card-row2">
            <KnowledgeDot conf={conf} />
            <span className="card-subject">{abbrev}</span>
          </div>

          {/* Row 3: L2 text (standard/full only) */}
          {(level === 'standard' || level === 'full') && e.general_topic && (
            <div className="card-l2-row">{e.general_topic}</div>
          )}

          {/* Row 4: size bars + revise pill (standard/full only) */}
          {(level === 'standard' || level === 'full') && (
            <div className="card-row3">
              <SizeBars principleCount={e.principle_count} />
              {isRevision && <span className="pill-revise" style={{ marginLeft: 'auto' }}>Revise</span>}
            </div>
          )}

          {/* Row 4: stats (full only) */}
          {level === 'full' && (
            <div className="card-extra">
              <div className="card-stat">
                <span className="card-stat-label">Questions</span>
                {e.questions_attempted > 0 ? (
                  <span className={`card-stat-value ${e.questions_correct / e.questions_attempted >= 0.7 ? 'card-stat-green' : e.questions_correct / e.questions_attempted >= 0.4 ? 'card-stat-amber' : 'card-stat-red'}`}>
                    {e.questions_correct}/{e.questions_attempted}
                    <McqBar correct={e.questions_correct} total={Math.min(e.questions_attempted, 5)} />
                  </span>
                ) : (
                  <span className="card-stat-value card-stat-muted">None yet</span>
                )}
              </div>
              <div className="card-stat">
                <span className="card-stat-label">Last seen</span>
                <span className={`card-stat-value ${lastSeenColour(e.date_updated)}`}>
                  {formatLastSeen(e.date_updated)}
                </span>
              </div>
            </div>
          )}
        </div>

        {canTick && (
          <div className="card-actions">
            {actionButtons}
            {isRevision && e.topic_id && (
              <button
                className="qp-practice-link"
                onClick={ev => { ev.stopPropagation(); navigate(`${ROUTES.QUESTIONS}?mode=topic&topic_id=${e.topic_id}`); }}
              >
                Practice
              </button>
            )}
          </div>
        )}
      </div>
      {completing && <TickOverlay entry={e} onConfirm={handleConfirm} onCancel={() => setCompleting(false)} />}
    </>
  );
}
